import type { Page } from '@hello-aria/makalu-api-client'
import { ComparisonFilterInput, QueryStringInput } from '@hello-aria/query-string/dist/stringify'
import {
    useInfiniteQuery,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query'
import { isDate, isEmpty, isNil, isNumber, isString } from 'lodash'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'
import { homeTableFields } from '../../modules/home/enums/homeEnums'
import { DEFAULT_PAGE_SIZE } from '../enums/constants'
import { CompanyPerCurrencyConfig } from '../models/companyModel'
import { Identifier } from '../models/debtorModels'
import { Invoice, isInvoice } from '../models/invoiceModels'
import {
    CancelLoan,
    CreateLoanPayload,
    CreatePaymentRetry,
    DeleteLoan,
    Loan,
    LoanGateway,
    LoansExportDatesGateway,
    UpdateLoanAttachments,
    UpdateLoanPayload
} from '../models/loanModels'
import { QueryParams } from '../models/queryModels'
import { Receivable } from '../models/receivableModels'
import { User } from '../models/userModel'
import {
    cancelLoan,
    createLoanPaymentRetry,
    createLoan,
    deleteLoan,
    getLoan,
    listLoansOfInvoice,
    listLoansOfUser,
    listLoans,
    updateLoan
} from '../services/loansServices'
import { State, updateLoanAttachments } from '../services/uploadServices'
import { isISODateString, localizeDate } from '../utils/dateUtils'
import { resolveLoanFilters } from '../utils/loansUtils'
import { getPageSize, mapQuery } from '../utils/queryUtils'
import { HttpError } from '@hello-aria/makalu-api-client'
import { removeWhiteSpace } from '../utils/stringUtils'
import { useMe } from './useMe'
import { useQueryParamsState } from './useQueryParams'

export const loansQueryKeys = {
    root: ['loans'],
    loans: (companyId: string): string[] => [...loansQueryKeys.root, companyId],
    infiniteLoans: (
        companyId: string,
        query?: QueryStringInput,
        isEnabled?: boolean
    ): (string | QueryStringInput | boolean | undefined)[] => [...loansQueryKeys.root, companyId, query, isEnabled],
    depreacted_infiniteLoans: (companyId: string, queryParams?: QueryParams): (string | QueryParams | undefined)[] => [
        ...loansQueryKeys.root,
        companyId,
        queryParams
    ],
    infiniteLoansByUserId: (
        companyId: string,
        userId: string,
        queryParams?: QueryParams
    ): (string | QueryParams | undefined)[] => [...loansQueryKeys.root, companyId, userId, queryParams],
    loanById: (companyId: string, loanId: string | null | undefined): (undefined | null | string)[] => [
        ...loansQueryKeys.root,
        companyId,
        loanId
    ],
    loansByInvoiceId: (companyId: string, invoiceId: string | undefined): (undefined | string)[] => [
        ...loansQueryKeys.root,
        companyId,
        invoiceId
    ],
    loansCsv: (
        companyId: string,
        dates: LoansExportDatesGateway | undefined
    ): (string | undefined | LoansExportDatesGateway)[] => [companyId, dates]
}

interface UseInfiniteLoans {
    query?: QueryStringInput
    isEnabled?: boolean
}

export const useInfiniteLoans = (options?: UseInfiniteLoans) => {
    const { query, isEnabled } = options || {}
    const { companyId } = useMe()

    return useInfiniteQuery({
        queryKey: loansQueryKeys.infiniteLoans(companyId, query, isEnabled),

        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            listLoans({
                companyId,
                hasAttachments: false,
                query: {
                    ...query,
                    pagination: {
                        after: pageParam,
                        first: getPageSize(query)
                    }
                }
            }),

        enabled: options?.isEnabled ?? true,

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

interface deprecated_UseInfiniteLoans {
    isEnabled?: boolean
    customFilters?: ComparisonFilterInput[]
    pageSize?: number
}

export const deprecated_useInfiniteLoans = (options?: deprecated_UseInfiniteLoans) => {
    const { customFilters } = options || {}
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()

    const filters: ComparisonFilterInput[] = customFilters ?? []

    return useInfiniteQuery({
        queryKey: loansQueryKeys.depreacted_infiniteLoans(
            companyId,
            queryParamsState.pick([`loans_search`, `loans_sort`, `loans_filter`])
        ),

        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            listLoans({
                companyId,
                hasAttachments: false,
                query: mapQuery({
                    filter: resolveLoanFilters({
                        customFilters: [...filters, ...(queryParamsState.get('loans_filter') ?? [])]
                    }),
                    search: { value: queryParamsState.get('loans_search'), path: homeTableFields },
                    sortBy: queryParamsState.get('loans_sort'),
                    pagination: { after: pageParam, first: options?.pageSize ?? DEFAULT_PAGE_SIZE }
                })
            }),

        enabled: options?.isEnabled ?? true,
        refetchOnMount: true,

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

interface UseInfiniteLoansByUserId {
    userId: User['id']
}

export const useInfiniteLoansByUserId = ({ userId }: UseInfiniteLoansByUserId) => {
    const { companyId } = useMe()
    const queryParamsState = useQueryParamsState()

    return useInfiniteQuery({
        queryKey: loansQueryKeys.infiniteLoansByUserId(
            companyId,
            userId,
            queryParamsState.pick(['loans_search', 'loans_sort'])
        ),

        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            listLoansOfUser({
                userId,
                companyId,
                hasAttachments: false,
                query: mapQuery({
                    search: { value: queryParamsState.get('loans_search'), path: homeTableFields },
                    sortBy: queryParamsState.get('loans_sort'),
                    pagination: { after: pageParam }
                })
            }),

        getNextPageParam: (lastPage) => {
            if (lastPage?.pagination?.hasNextPage) return lastPage?.pagination?.endCursor
            return undefined
        },

        initialPageParam: undefined
    })
}

export const useLoanById = (loanId: string | undefined | null): UseQueryResult<Loan | undefined> => {
    const { companyId } = useMe()
    return useQuery({
        queryKey: loansQueryKeys.loanById(companyId, loanId),
        queryFn: () => (!isNil(loanId) ? getLoan({ companyId, loanId }) : undefined),
        enabled: !isNil(loanId)
    })
}

export const useLoansByInvoiceId = (invoiceId: string | undefined): UseQueryResult<Page<Loan> | undefined> => {
    const { companyId } = useMe()

    return useQuery({
        queryKey: loansQueryKeys.loansByInvoiceId(companyId, invoiceId),
        queryFn: () => listLoansOfInvoice({ companyId, invoiceId }),
        enabled: !isNil(invoiceId)
    })
}

export const useCreateLoan = (): UseMutationResult<LoanGateway | undefined, unknown, CreateLoanPayload> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: CreateLoanPayload) =>
            createLoan(companyId, {
                userId: payload.userId,
                invoiceId: payload.invoiceId,
                receivableId: payload.receivableId,
                amount: payload.amount,
                currency: payload.currency,
                attachments: payload.attachments,
                payoutLabel: payload.payoutLabel,
                paymentDestination: payload.paymentDestination,
                guarantor: payload.guarantor
            })
    })
}

const sanitizeValue = (value: unknown): Date | number | string | null => {
    if (isDate(value) || isNumber(value)) return value

    if (isString(value) && !isEmpty(value)) {
        if (isISODateString(value)) return new Date(value)

        return value
    }

    return null
}

const resolveValuesInOverrideLabel = (
    overrideLabel: CompanyPerCurrencyConfig['overrideLoanPayoutLabel'],
    invoice: MappedInvoice
) => {
    return Object.entries(invoice).reduce((acc, [key, value]) => {
        if (isString(overrideLabel) && overrideLabel.includes(key)) {
            acc = { ...acc, [key]: value }
        }

        return acc
    }, {})
}

const formatValue = (value: string | number | Date | undefined, locale: string) => {
    if (value instanceof Date) return localizeDate(value, { locale })
    if (isNumber(value)) return value.toString()
    return value
}

interface MappedInvoice {
    invoiceNumber: string | undefined
    customerName: string | undefined
    customerSIREN: string | undefined
    billDate: string | undefined
    amount: number | undefined
}

export type UseLoanPayoutLabelInvoice = Partial<
    Pick<Invoice, 'invoiceNumber' | 'debtorName' | 'invoiceDate' | 'totalAmountIncludingTaxes' | 'currency'> & {
        debtorIdentifier: Partial<Identifier>
    }
>

export const useLoanPayoutLabel = (
    overrideLabel: string | undefined,
    billingDocument: Invoice | Receivable | undefined,
    locale: string
): { payoutLabel: string | undefined; hasWarningLabel: boolean } => {
    const t = useTranslations('createLoans')
    const [hasWarningLabel, setHasWarningLabel] = useState(false)

    if (!isInvoice(billingDocument)) {
        return { payoutLabel: undefined, hasWarningLabel: false }
    }

    const mappedInvoice: MappedInvoice = {
        invoiceNumber: billingDocument.invoiceNumber,
        customerName: billingDocument.debtorName,
        customerSIREN: billingDocument.debtorIdentifier?.value,
        billDate: billingDocument.invoiceDate,
        amount: billingDocument.totalAmountIncludingTaxes
    }

    const label = overrideLabel ?? ''

    const firstRule = Object.keys(mappedInvoice).map((key) => `{{ ${key} }}`)
    const secondRule = Object.keys(mappedInvoice).map((key) => `{{${key}}}`)

    const regex = new RegExp([...firstRule, ...secondRule].join('|'), 'g')

    const values = resolveValuesInOverrideLabel(overrideLabel, mappedInvoice)
    const unvalidValues = Object.values(values).filter((value) => !sanitizeValue(value))
    const hasUnvalidValues = !isEmpty(unvalidValues)

    useEffect(() => {
        setHasWarningLabel(() => hasUnvalidValues)
    }, [hasUnvalidValues])

    const formattedLabel = label.replace(regex, (matched) => {
        const sanitizedMatched = removeWhiteSpace(matched)
        const key = sanitizedMatched.match(/{{([^}]+)}}/)?.[1] as keyof MappedInvoice
        const value = sanitizeValue(mappedInvoice[key])

        if (key && value) {
            return formatValue(value, locale) ?? ''
        } else {
            return `{{ ${t(`payoutLabel.${key}` as any)} }}`
        }
    })

    return { payoutLabel: formattedLabel, hasWarningLabel }
}

export const useUpdateLoan = (): UseMutationResult<Loan | undefined, HttpError, UpdateLoanPayload> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: UpdateLoanPayload) => updateLoan(companyId, payload),
        onSuccess: (_, { loanId }) =>
            queryClient.invalidateQueries({ queryKey: loansQueryKeys.loanById(companyId, loanId) })
    })
}

export const useUpdateLoanAttachments = (
    state: State
): UseMutationResult<LoanGateway, unknown, UpdateLoanAttachments> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload) => updateLoanAttachments(companyId, payload, state),
        onSuccess: (_, { id }) => queryClient.invalidateQueries({ queryKey: loansQueryKeys.loanById(companyId, id) })
    })
}

export const useCancelLoan = (): UseMutationResult<unknown, unknown, Pick<CancelLoan, 'loanId'>> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: Pick<CancelLoan, 'loanId'>) => cancelLoan({ ...payload, companyId }),
        onSuccess: (_, { loanId }) =>
            queryClient.invalidateQueries({ queryKey: loansQueryKeys.loanById(companyId, loanId) })
    })
}

export const useDeleteLoan = (): UseMutationResult<unknown, unknown, Pick<DeleteLoan, 'loanId'>> => {
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: Pick<DeleteLoan, 'loanId'>) => deleteLoan({ ...payload, companyId })
    })
}

export const useCreateLoanPaymentRetry = (): UseMutationResult<LoanGateway | undefined, Error, CreatePaymentRetry> => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    return useMutation({
        mutationFn: (payload: CreatePaymentRetry) => createLoanPaymentRetry(payload, companyId),
        onSuccess: (_, { loanId }) =>
            queryClient.invalidateQueries({ queryKey: loansQueryKeys.loanById(companyId, loanId) })
    })
}

export const useInvalidateLoans = () => {
    const queryClient = useQueryClient()
    const { companyId } = useMe()

    const invalidateLoans = async () => {
        await queryClient.invalidateQueries({ queryKey: loansQueryKeys.root })
    }

    const invalidateLoan = async (loanId: string) => {
        await queryClient.invalidateQueries({ queryKey: loansQueryKeys.loanById(companyId, loanId) })
    }

    return {
        invalidateLoans,
        invalidateLoan
    }
}
